/**
 * A filled form's comment threads: one TanStack query shared by the Comments
 * pane and the page's pins (both call this hook with the same form), patched
 * with each thread a write returns.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { queryClient } from "@/lib/query/queryClient";

import { FormCommentsAPI } from "../commentsApi";
import {
  commentPinsByField,
  filterCounts,
  upsertThread,
  type CommentPinInfo,
  type FormCommentFilter,
  type FormCommentThread,
  type NewFormComment
} from "../commentsModel";

export const formCommentsQueryKey = (
  individualId: string,
  filledFormId: string
) => ["form-comments", individualId, filledFormId] as const;

export type FormCommentsState = {
  threads: FormCommentThread[];
  counts: Record<FormCommentFilter, number>;
  pins: Record<string, CommentPinInfo>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  create: (payload: NewFormComment) => Promise<FormCommentThread>;
  reply: (threadId: string, body: string) => Promise<FormCommentThread>;
  setResolved: (
    threadId: string,
    resolved: boolean
  ) => Promise<FormCommentThread>;
  editBody: (threadId: string, body: string) => Promise<FormCommentThread>;
};

const NO_THREADS: FormCommentThread[] = [];

export const useFormComments = (
  individualId: string | null,
  filledFormId: string | null
): FormCommentsState => {
  const ind = individualId ?? "";
  const form = filledFormId ?? "";
  const query = useQuery(
    {
      queryKey: formCommentsQueryKey(ind, form),
      queryFn: () => FormCommentsAPI.list(ind, form),
      enabled: Boolean(ind && form)
    },
    queryClient
  );
  const threads = query.data ?? NO_THREADS;

  const put = useCallback(
    (thread: FormCommentThread) => {
      queryClient.setQueryData<FormCommentThread[]>(
        formCommentsQueryKey(ind, form),
        (current) => upsertThread(current ?? [], thread)
      );
      return thread;
    },
    [ind, form]
  );

  const create = useCallback(
    async (payload: NewFormComment) =>
      put(await FormCommentsAPI.create(ind, form, payload)),
    [ind, form, put]
  );
  const reply = useCallback(
    async (threadId: string, body: string) =>
      put(await FormCommentsAPI.reply(ind, form, threadId, body)),
    [ind, form, put]
  );
  const setResolved = useCallback(
    async (threadId: string, resolved: boolean) =>
      put(await FormCommentsAPI.update(ind, form, threadId, { resolved })),
    [ind, form, put]
  );
  const editBody = useCallback(
    async (threadId: string, body: string) =>
      put(await FormCommentsAPI.update(ind, form, threadId, { body })),
    [ind, form, put]
  );
  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: formCommentsQueryKey(ind, form)
    });
  }, [ind, form]);

  const counts = useMemo(() => filterCounts(threads), [threads]);
  const pins = useMemo(() => commentPinsByField(threads), [threads]);

  return {
    threads,
    counts,
    pins,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Could not load the comments."
      : null,
    refetch,
    create,
    reply,
    setResolved,
    editBody
  };
};
